import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { FormikErrorMessage, useFormikFieldError } from "./FormikErrorMessage";
import type { FormikProps } from "formik";
import { getIn } from "formik";

interface BaseFormikFieldProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formik: FormikProps<any>;
  name: string;
  label: string;
  className?: string;
}

interface FormikInputProps extends BaseFormikFieldProps {
  type?: "text" | "number" | "email";
  placeholder?: string;
  min?: string;
  max?: string;
  step?: string;
  uppercase?: boolean;
  value?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

interface FormikSelectProps extends BaseFormikFieldProps {
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
}

interface FormikSwitchProps extends BaseFormikFieldProps {
  description?: string;
}

/**
 * Composant Input intégré avec Formik
 */
export const FormikInput: React.FC<FormikInputProps> = ({
  formik,
  name,
  label,
  type = "text",
  placeholder,
  min,
  max,
  step,
  className = "",
  uppercase = false,
  value,
  onChange,
}) => {
  const { errorClass } = useFormikFieldError(formik, name);

  return (
    <div className={`space-y-2 ${className}`}>
      <Label htmlFor={name}>{label}</Label>
      <Input
        id={name}
        name={name}
        type={type}
        min={min}
        max={max}
        step={step}
        placeholder={placeholder}
        value={value || getIn(formik.values, name)?.toString() || ""}
        onChange={(e) =>
          uppercase
            ? onChange
              ? onChange(e)
              : formik.setFieldValue(name, e.target.value.toUpperCase())
            : onChange
              ? onChange(e)
              : formik.handleChange(e)
        }
        onBlur={formik.handleBlur}
        className={errorClass}
      />
      <FormikErrorMessage formik={formik} name={name} />
    </div>
  );
};

/**
 * Composant Select intégré avec Formik
 */
export const FormikSelect: React.FC<FormikSelectProps> = ({
  formik,
  name,
  label,
  options,
  placeholder,
  className = "",
}) => {
  const { errorClass } = useFormikFieldError(formik, name);

  return (
    <div className={`space-y-2 ${className}`}>
      <Label htmlFor={name}>{label}</Label>
      <Select
        value={getIn(formik.values, name)?.toString() || ""}
        onValueChange={(value) => formik.setFieldValue(name, value)}
      >
        <SelectTrigger className={errorClass}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <FormikErrorMessage formik={formik} name={name} />
    </div>
  );
};

/**
 * Composant Switch intégré avec Formik
 */
export const FormikSwitch: React.FC<FormikSwitchProps> = ({
  formik,
  name,
  label,
  description,
  className = "",
}) => {
  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center space-x-3">
        <Switch
          id={name}
          checked={Boolean(getIn(formik.values, name))}
          onCheckedChange={(checked) => formik.setFieldValue(name, checked)}
        />
        <Label htmlFor={name} className="text-sm font-medium">
          {label}
        </Label>
      </div>
      {description && (
        <p className="text-xs text-muted-foreground ml-7">{description}</p>
      )}
      <FormikErrorMessage formik={formik} name={name} />
    </div>
  );
};

/**
 * @deprecated Use FormikSwitch instead
 * Composant Checkbox intégré avec Formik (deprecated)
 */
export const FormikCheckbox: React.FC<FormikSwitchProps> = FormikSwitch;
